import { useState } from 'react';
import { useGetBillingBalanceQuery, useCreateCheckoutSessionMutation } from '@/features/admin/api/billingApi';
import { PLANS, ANNUAL_DISCOUNT, formatPrice } from '@/features/admin/config/billingConfig';
import { BillingCycle, PlanConfig } from '@/types/billing.types';
import { Zap } from 'lucide-react';
import { PlansSectionSkeleton } from '@/features/admin/components/loaders/BillingSkeletons';
import Swal from 'sweetalert2';

export function PlansSection() {
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);

  const { data: balanceData, isLoading: isBalanceLoading } = useGetBillingBalanceQuery();
  const [createCheckout, { isLoading: isCheckoutLoading }] = useCreateCheckoutSessionMutation();

  const currentPlan = balanceData?.data?.plan ?? 'freemium';

  const getPriceId = (plan: PlanConfig) =>
    cycle === 'annual' ? plan.stripePriceAnnual : plan.stripePriceMonthly;

  const getDisplayPrice = (plan: PlanConfig) =>
    cycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;

  const handleSelectPlan = async (priceId: string) => {
    if (!priceId) return;
    try {
      const result = await createCheckout({ price_id: priceId }).unwrap();
      if (result?.success === false) {
        Swal.fire({
          icon: 'error',
          title: 'Checkout Error',
          text: result.message || 'Failed to initialize checkout.',
          background: 'var(--admin-card-bg)',
          color: 'var(--admin-card-text)',
          confirmButtonColor: '#3E8FA6',
        });
        return;
      }
      if (result?.data?.url) {
        window.location.href = result.data.url;
      }
    } catch (error: any) {
      console.error('Failed to create checkout session:', error);
      Swal.fire({
        icon: 'error',
        title: 'Checkout Error',
        text: error?.data?.message || error?.message || 'An unexpected error occurred.',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)',
        confirmButtonColor: '#3E8FA6',
      });
    }
  };

  if (isBalanceLoading) {
    return <PlansSectionSkeleton />;
  }

  return (
    <div>
      {/* Billing cycle toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '32px',
        }}
      >
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '14px', color: 'var(--admin-card-subtext)' }}>
          Billing cycle
        </span>
        <div 
          style={{ 
            display: 'inline-flex', 
            background: 'var(--admin-toggle-bg)',
            borderRadius: '8px',
            padding: '3px',
            border: '1px solid var(--admin-card-border)',
            gap: '4px'
          }}
        >
          {(['monthly', 'annual'] as BillingCycle[]).map((c) => {
            const isActive = cycle === c;
            return (
              <button
                key={c}
                onClick={() => setCycle(c)}
                onMouseEnter={() => setHoveredTab(c)}
                onMouseLeave={() => setHoveredTab(null)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  background: isActive 
                    ? 'var(--admin-toggle-active-bg)' 
                    : hoveredTab === c 
                      ? 'var(--color-glass-surface, rgba(255,255,255,0.03))' 
                      : 'transparent',
                  boxShadow: isActive ? 'var(--admin-toggle-active-shadow)' : 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  color: isActive 
                    ? 'var(--admin-card-text)' 
                    : 'var(--admin-card-subtext)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.15s ease',
                  textTransform: 'capitalize',
                }}
              >
                {c}
                {c === 'annual' && (
                  <span
                    style={{
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#10B981',
                      background: 'rgba(16,185,129,0.15)',
                      borderRadius: '4px',
                      padding: '1px 5px',
                    }}
                  >
                    -{Math.round(ANNUAL_DISCOUNT * 100)}%
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Plan cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '16px',
        }}
      >
        {PLANS.map((plan, index) => {
          const currentPlanIndex = PLANS.findIndex(p => p.id === currentPlan);
          const currentCycle = balanceData?.data?.billing_cycle ?? 'monthly';
          const isExactCurrent = currentPlan === plan.id && currentCycle === cycle;
          const isDowngrade = index < currentPlanIndex;
          
          const priceId = getPriceId(plan);
          const displayPrice = getDisplayPrice(plan);

          let buttonText = "Upgrade";
          let isDisabled = isCheckoutLoading || !priceId;
          
          if (isExactCurrent) {
            buttonText = "Current Plan";
            isDisabled = true;
          } else if (currentPlan === plan.id && cycle === 'annual' && currentCycle === 'monthly') {
            buttonText = "Upgrade to Annual";
          } else if (currentPlan === plan.id && cycle === 'monthly' && currentCycle === 'annual') {
            buttonText = "Downgrade";
            isDisabled = true; // Use customer portal for downgrades
          } else if (isDowngrade) {
            buttonText = "Upgrade";
            isDisabled = true; // Use customer portal for downgrades
          }

          return (
            <div
              key={plan.id}
              style={{
                background: (currentPlan === plan.id)
                  ? 'rgba(148, 163, 184, 0.03)' 
                  : 'var(--admin-card-bg)',
                border: `1px solid ${
                  (currentPlan === plan.id)
                    ? 'rgba(148, 163, 184, 0.4)' 
                    : plan.isPopular
                    ? 'rgba(56, 189, 248, 0.4)'
                    : 'var(--admin-card-border)'
                }`,
                boxShadow: (currentPlan === plan.id)
                  ? '0 0 20px rgba(148, 163, 184, 0.05)'
                  : 'none',
                borderRadius: '0',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                position: 'relative',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
            >
              {plan.isPopular && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--color-status-sky, #38bdf8)',
                    color: '#fff',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '11px',
                    fontWeight: 700,
                    borderRadius: '0',
                    padding: '3px 12px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Most Popular
                </span>
              )}

              <div>
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: '18px',
                    color: 'var(--admin-card-text)',
                    margin: 0,
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  {plan.name}
                </p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: 'var(--admin-card-subtext)', margin: '4px 0 0' }}>
                  {plan.description}
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                  <span
                    style={{
                      fontSize: '32px',
                      fontWeight: 800,
                      color: 'var(--admin-card-text)',
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}
                  >
                    {formatPrice(displayPrice)}
                  </span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: 'var(--admin-card-subtext)' }}>
                    {plan.seats > 1 ? `/seat/mo` : `/mo`}
                  </span>
                </div>
                {cycle === 'annual' && (
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', color: 'var(--color-status-sky, #38bdf8)', fontWeight: 600 }}>
                    Billed {formatPrice(plan.annualPrice * 12 * plan.seats)} yearly
                  </span>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  borderRadius: '0',
                  background: 'rgba(56,189,248,0.06)',
                  border: '1px solid rgba(56,189,248,0.12)',
                }}
              >
                <Zap size={13} color="var(--color-status-sky, #38bdf8)" />
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: 600, color: 'var(--admin-card-text)' }}>
                  {plan.creditsPerCycle.toLocaleString()} credits / mo
                  {plan.seats > 1 && ` (${plan.seats} seats)`}
                </span>
              </div>

              <button
                onClick={() => handleSelectPlan(priceId)}
                disabled={isDisabled}
                style={{
                  padding: '10px',
                  borderRadius: '0',
                  border: isDisabled && !isExactCurrent ? '1px solid var(--admin-card-border)' : isExactCurrent ? '1px solid var(--admin-card-border)' : 'none',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  fontWeight: 700,
                  fontSize: '14px',
                  fontFamily: "'Inter', sans-serif",
                  transition: 'all 0.2s',
                  background: isExactCurrent
                    ? 'transparent'
                    : isDisabled
                    ? 'var(--admin-card-bg)'
                    : plan.isPopular
                    ? 'var(--color-status-sky, #38bdf8)'
                    : '#ffffff',
                  color: isExactCurrent || isDisabled
                    ? 'var(--admin-card-subtext)'
                    : plan.isPopular
                    ? '#fff'
                    : '#000',
                  marginTop: 'auto',
                }}
              >
                {buttonText}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
